// @ts-strict-ignore
import { Text } from '@backend/lib-design-system';
import { ArrowBackFilledIcon, ArrowNextFilledIcon } from '@backend/lib-design-system/icons';
import type { Dispatch, SetStateAction } from 'react';
import { styled } from 'styled-components';

import { useTranslation } from '~/hooks';
import { getMediaQuery } from '~/utils/responsive/media';

import { MenuDropDown } from './MenuDropDown';
import { MenuDropdownLink } from './MenuDropdownLink';
import type { MenuItem } from './types';

const StyledDropdownGrid = styled.div<{ $count: number }>`
  display: grid;

  grid-auto-flow: column;
  grid-template-rows: repeat(7, auto);
  grid-auto-columns: 1fr;

  gap: 8px;

  ${getMediaQuery('tablet')} {
    grid-template-rows: repeat(10, auto);
  }

  ${getMediaQuery('mobile')} {
    grid-template-rows: repeat(${({ $count }) => $count}, auto);
  }
`;

export const MenuButton = styled(Text)`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 16px;
  color: white;
  font-size: 20px;
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MenuPreviousButton = styled(MenuButton)`
  ${getMediaQuery('laptop', 'desktop')} {
    display: none;
  }
`;

type MenuDropdownItemsProps = {
  menuItems: MenuItem[];
  headerHeight: number;
  selectedMenuName: string | undefined;
  setSelectedMenuName: Dispatch<SetStateAction<string | undefined>>;
};

export const MenuDropdownItems = ({
  menuItems,
  selectedMenuName,
  headerHeight,
  setSelectedMenuName,
}: MenuDropdownItemsProps) => {
  const t = useTranslation();

  return (
    <>
      {menuItems.map(({ name, items, link }, menuItemIndex) => {
        const isSelected = name === selectedMenuName;
        return (
          <MenuDropDown
            isOpen={isSelected}
            key={menuItemIndex}
            headerHeight={headerHeight}
            onClick={() => {
              setSelectedMenuName(undefined);
            }}
          >
            {isSelected && (
              <MenuPreviousButton
                variant="body-light"
                onClick={event => {
                  event.stopPropagation();
                  setSelectedMenuName('');
                }}
              >
                <ArrowBackFilledIcon size="l" />
                {t(`menu.${name}`)}
              </MenuPreviousButton>
            )}
            <StyledDropdownGrid $count={items.length}>
              {items.map(({ label, href }, index) => (
                <MenuDropdownLink key={index} href={href}>
                  {label}
                </MenuDropdownLink>
              ))}
            </StyledDropdownGrid>
            {Boolean(link) && (
              <MenuDropdownLink href={link}>
                {t(`menu.link.${name}`)}
                <ArrowNextFilledIcon size="s" />
              </MenuDropdownLink>
            )}
          </MenuDropDown>
        );
      })}
    </>
  );
};
